import type { GameIntent, AlertLevel, ViewscreenMode, SceneAction, ScanReadoutData } from "../types/game";
import type { SceneCommandExecutor } from "./SceneCommandExecutor";
import type { TimerSystem } from "./TimerSystem";
import { SCENE, IMPULSE_SPEEDS, SPEED_LABEL_TO_IMPULSE } from "./config";
import { setCameraMode } from "./cameraState";

let orderCounter = 0;
function nextOrderId(prefix: string) {
  return `${prefix}_${Date.now()}_${orderCounter++}`;
}

interface Callbacks {
  setAlertLevel: (level: AlertLevel) => void;
  setViewscreenMode: (mode: ViewscreenMode) => void;
  setHailState?: (state: { active: boolean; channelMuted: boolean; targetId: string | null; targetName: string | null }) => void;
  fetchPortrait: (characterId: string, description: string) => void;
  addLogEntry?: (text: string) => void;
  setShowChart?: (show: boolean) => void;
  addLockedTarget?: (targetId: string) => void;
  updateShipSystems?: (updater: (ship: import("../types/game").PlayerShip) => void) => void;
}

export class IntentResolver {
  private executor: SceneCommandExecutor;
  private timer: TimerSystem;
  private callbacks: Callbacks;
  private weaponMode: string = "precision";
  private activeRepairs: Map<string, ReturnType<typeof setInterval>> = new Map();

  constructor(executor: SceneCommandExecutor, timer: TimerSystem, callbacks: Callbacks) {
    this.executor  = executor;
    this.timer     = timer;
    this.callbacks = callbacks;
  }

  resolve(intents: GameIntent[]) {
    for (const intent of intents) {
      try {
        this.resolveOne(intent);
      } catch (e) {
        console.warn(`[IntentResolver] Failed to resolve intent "${intent.type}":`, e);
      }
    }
  }

  // Try to acquire a target lock, but only if the target is a real entity
  // that isn't us. Prevents stale "TARGET LOST" panels caused by the LLM
  // emitting lock_target/scan/arm_weapons against player_ship or unknown ids.
  private safeLockTarget(target: string | undefined) {
    if (!target || target === "player_ship") return;
    const exists = this.executor.currentSpatialState.entities.some(e => e.id === target);
    if (!exists) return;
    this.callbacks.addLockedTarget?.(target);
  }

  private resolveOne(intent: GameIntent) {
    const delay = intent.delay ?? 0;

    switch (intent.type) {

      // ── MOVEMENT ──

      case "approach": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] approach: no target"); break; }
        const speed = intent.speed ?? "slow";
        const impulse = SPEED_LABEL_TO_IMPULSE[speed];
        const unitsPerSec = IMPULSE_SPEEDS[SPEED_LABEL_TO_IMPULSE[speed]];

        const from = this.executor.currentSpatialState.playerShip.position;
        const dest = this.resolveOrbitPosition(target);
        const dist = vec3Dist(from, dest);
        const duration = Math.max(4, dist / unitsPerSec);

        const orderId = nextOrderId("approach");
        const targetName = this.getEntityName(target) ?? target;

        this.enqueue([{
          action: "move_to",
          params: { entityId: "player_ship", destination: dest, speed: impulse, duration },
          delay,
        }]);

        this.timer.processOrders([{
          id: orderId,
          action: "add",
          label: `Approach ${targetName}`,
          duration,
        }]);
        break;
      }

      case "orbit": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] orbit: no target"); break; }
        const orbitSpeed = intent.speed ?? "slow";
        const orbitImpulse = SPEED_LABEL_TO_IMPULSE[orbitSpeed];
        const orbitUnitsPerSec = IMPULSE_SPEEDS[SPEED_LABEL_TO_IMPULSE[orbitSpeed]];

        // Compute orbit approach point
        const orbitDest = this.resolveOrbitApproach(target, intent.orbitDistance);
        const orbitFrom = this.executor.currentSpatialState.playerShip.position;
        const orbitDist = vec3Dist(orbitFrom, orbitDest);
        const orbitDuration = Math.max(4, orbitDist / orbitUnitsPerSec);

        const orbitOrderId = nextOrderId("orbit");
        const orbitTargetName = this.getEntityName(target) ?? target;

        this.enqueue([{
          action: "move_to",
          params: {
            entityId: "player_ship",
            destination: orbitDest,
            speed: orbitImpulse,
            orbitOnArrival: target,
            duration: orbitDuration,
          },
          delay,
        }]);

        this.timer.processOrders([{
          id: orbitOrderId,
          action: "add",
          label: `Enter orbit of ${orbitTargetName}`,
          duration: orbitDuration,
        }]);
        break;
      }

      case "intercept": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] intercept: no target"); break; }
        const speed = intent.speed ?? "full";
        const impulse = SPEED_LABEL_TO_IMPULSE[speed];
        const unitsPerSec = IMPULSE_SPEEDS[SPEED_LABEL_TO_IMPULSE[speed]];

        const from = this.executor.currentSpatialState.playerShip.position;
        const targetPos = this.executor.currentSpatialState.entities.find(e => e.id === target)?.position;
        const dist = targetPos ? vec3Dist(from, targetPos) : 150;
        const duration = Math.max(4, dist / unitsPerSec);

        const orderId = nextOrderId("intercept");
        const targetName = this.getEntityName(target) ?? target;

        this.enqueue([{
          action: "move_to",
          params: { entityId: "player_ship", destination: target, speed: impulse, duration },
          delay,
        }]);

        this.timer.processOrders([{
          id: orderId,
          action: "add",
          label: `Intercept ${targetName}`,
          duration,
        }]);
        break;
      }

      case "retreat": {
        const speed = intent.speed ?? "full";
        const impulse = SPEED_LABEL_TO_IMPULSE[speed];
        const unitsPerSec = IMPULSE_SPEEDS[SPEED_LABEL_TO_IMPULSE[speed]];

        const from = this.executor.currentSpatialState.playerShip.position;
        const edge = SCENE.SYSTEM_EDGE;
        const dist = vec3Dist(from, edge);
        const duration = Math.max(4, dist / unitsPerSec);

        const orderId = nextOrderId("retreat");
        this.enqueue([{ action: "move_to", params: { entityId: "player_ship", destination: "system_edge", speed: impulse, duration }, delay }]);
        this.timer.processOrders([{ id: orderId, action: "add", label: "Retreat to system edge", duration }]);
        break;
      }

      case "all_stop": {
        // Cancel orders immediately so subsequent intents in this batch
        // can add new orders without being clobbered by the deferred stop callback
        this.timer.cancelAllOrders();
        this.enqueue([{ action: "all_stop", params: { entityId: "player_ship" }, delay }]);
        break;
      }

      case "face": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] face: no target"); break; }
        this.enqueue([{ action: "rotate", params: { entityId: "player_ship", heading: target, durationSeconds: 12 }, delay }]);
        break;
      }

      case "position_between": {
        const a = intent.target;
        const b = intent.target2;
        if (!a || !b) { console.warn("[IntentResolver] position_between: need target and target2"); break; }

        const posA = this.resolveEntityPosition(a);
        const posB = this.resolveEntityPosition(b);
        if (!posA || !posB) { console.warn("[IntentResolver] position_between: could not resolve positions"); break; }

        // True midpoint between the two entities
        const mid = {
          x: (posA.x + posB.x) / 2,
          y: (posA.y + posB.y) / 2,
          z: (posA.z + posB.z) / 2,
        };

        const speed = intent.speed ?? "half";
        const impulse = SPEED_LABEL_TO_IMPULSE[speed];
        const unitsPerSec = IMPULSE_SPEEDS[SPEED_LABEL_TO_IMPULSE[speed]];
        const from = this.executor.currentSpatialState.playerShip.position;
        const dist = vec3Dist(from, mid);
        const duration = Math.max(4, dist / unitsPerSec);

        const orderId = nextOrderId("position_between");
        const nameA = this.getEntityName(a) ?? a;
        const nameB = this.getEntityName(b) ?? b;

        this.enqueue([{
          action: "move_to",
          params: { entityId: "player_ship", destination: mid, speed: impulse, duration },
          delay,
        }]);

        this.timer.processOrders([{
          id: orderId,
          action: "add",
          label: `Move between ${nameA} and ${nameB}`,
          duration,
        }]);
        break;
      }

      case "evasive_maneuvers": {
        const duration = 8;
        const orderId = nextOrderId("evasive");
        this.enqueue([{ action: "evasive", params: { entityId: "player_ship", durationSeconds: duration }, delay }]);
        this.timer.processOrders([{ id: orderId, action: "add", label: "Evasive maneuvers", duration }]);
        this.callbacks.addLogEntry?.("Evasive maneuvers engaged.");
        break;
      }

      // ── TARGETING ──

      case "lock_target": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] lock_target: no target"); break; }
        // Don't lock on yourself, and don't lock on entities that don't exist
        // in the spatial state — those just produce stale "TARGET LOST" panels.
        if (target === "player_ship") { console.warn("[IntentResolver] lock_target: refused self-lock"); break; }
        const exists = this.executor.currentSpatialState.entities.some(e => e.id === target);
        if (!exists) { console.warn(`[IntentResolver] lock_target: entity "${target}" not in spatial state — skipping`); break; }
        const targetName = this.getEntityName(target) ?? target;
        this.safeLockTarget(target);
        this.callbacks.addLogEntry?.(`Target lock acquired: ${targetName}.`);
        break;
      }

      case "unlock_target": {
        // Handled by UI — removeLockedTarget is on the panel
        break;
      }

      // ── COMBAT ──

      case "arm_weapons": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] arm_weapons: no target"); break; }
        const weaponLabel = intent.weaponType ?? "weapons";
        const targetName = this.getEntityName(target) ?? target;
        // Face the target, lock on, and update UI
        this.enqueue([{ action: "rotate", params: { entityId: "player_ship", heading: target, durationSeconds: 6 }, delay }]);
        this.safeLockTarget(target);
        this.callbacks.addLogEntry?.(`${weaponLabel.charAt(0).toUpperCase() + weaponLabel.slice(1)} locked on ${targetName}.`);
        break;
      }

      case "fire_weapons": {
        const target = intent.target ?? "velan_cruiser";
        const source = intent.source ?? "player_ship";
        const weaponType = intent.weaponType ?? "phaser";
        const hit = intent.hit ?? true;

        // Firing implies target lock
        if (source === "player_ship") this.safeLockTarget(target);

        const isBeam = ["phaser", "disruptor", "cutting_beam"].includes(weaponType);
        const targetName = this.getEntityName(target) ?? target;
        const sourceName = source === "player_ship" ? null : (this.getEntityName(source) ?? source);

        if (isBeam) {
          this.enqueue([{ action: "fire_beam", params: { sourceId: source, targetId: target, weaponType, durationSeconds: 2, hit }, delay }]);
          if (sourceName) {
            this.callbacks.addLogEntry?.(`${sourceName} firing ${weaponType} at ${source === "player_ship" ? "us" : targetName}!`);
          } else {
            this.callbacks.addLogEntry?.(`Firing ${weaponType} at ${targetName}.`);
          }
        } else {
          const count = intent.weaponCount ?? 1;
          this.enqueue([{ action: "fire_torpedo", params: { sourceId: source, targetId: target, torpedoType: weaponType, count, hit }, delay }]);
          if (sourceName) {
            this.callbacks.addLogEntry?.(`${sourceName} launching ${weaponType} torpedo${count > 1 ? "es" : ""} at ${source === "player_ship" ? "us" : targetName}!`);
          } else {
            this.callbacks.addLogEntry?.(`Launching ${count} ${weaponType} torpedo${count > 1 ? "es" : ""} at ${targetName}.`);
          }
        }

        if (hit) {
          this.enqueue([{ action: "shield_impact", params: { entityId: target, direction: "forward", intensity: 0.8 }, delay: delay + 0.5 }]);
          const subsys = intent.targetSubsystem ?? (this.weaponMode === "disable" ? "engines" : undefined);
          setTimeout(() => this.callbacks.addLogEntry?.(`Direct hit on ${targetName}${subsys ? ` (${subsys})` : ""}.`), (delay + 1) * 1000);
          if (intent.severity) {
            this.enqueue([{ action: "hull_damage", params: { entityId: target, severity: intent.severity, section: "hull", subsystem: subsys }, delay: delay + 1 }]);
          }
        } else {
          setTimeout(() => this.callbacks.addLogEntry?.(`Shot missed ${targetName}.`), (delay + 1.5) * 1000);
        }
        break;
      }

      case "raise_shields": {
        const entity = intent.source ?? "player_ship";
        this.enqueue([{ action: "raise_shields", params: { entityId: entity }, delay }]);
        break;
      }

      case "lower_shields": {
        const entity = intent.source ?? "player_ship";
        this.enqueue([{ action: "lower_shields", params: { entityId: entity }, delay }]);
        break;
      }

      case "red_alert": {
        this.enqueue([
          { action: "set_alert", params: { level: "red" }, delay },
          { action: "raise_shields", params: { entityId: "player_ship" }, delay },
        ]);
        this.callbacks.setAlertLevel("red");
        // Red alert implies weapons armed — update spatial state
        if (this.executor.currentSpatialState.playerShip) {
          (this.executor.currentSpatialState.playerShip as any).weapons = "armed";
        }
        break;
      }

      case "yellow_alert": {
        this.enqueue([{ action: "set_alert", params: { level: "yellow" }, delay }]);
        this.callbacks.setAlertLevel("yellow");
        break;
      }

      case "stand_down": {
        this.enqueue([
          { action: "set_alert", params: { level: "normal" }, delay },
          { action: "lower_shields", params: { entityId: "player_ship" }, delay },
        ]);
        this.callbacks.setAlertLevel("normal");
        break;
      }

      // ── SENSORS ──

      case "scan": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] scan: no target"); break; }
        const duration = 10;
        const orderId = nextOrderId("scan");
        const targetName = this.getEntityName(target) ?? target;
        this.enqueue([{ action: "scan_beam", params: { sourceId: "player_ship", targetId: target, durationSeconds: duration }, delay }]);
        this.timer.processOrders([{ id: orderId, action: "add", label: `Scanning ${targetName}`, duration }]);
        // Scanning implies tracking — acquire target lock
        this.safeLockTarget(target);
        break;
      }

      case "tractor_beam": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] tractor_beam: no target"); break; }
        this.enqueue([{ action: "tractor_beam", params: { sourceId: "player_ship", targetId: target, state: intent.state ?? "engage" }, delay }]);
        break;
      }

      // ── COMMUNICATION ──

      case "hail": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] hail: no target"); break; }
        const mode: ViewscreenMode = {
          mode: "hail",
          characterId: target,
          portraitPrompt: intent.portraitPrompt,
        };
        this.callbacks.setViewscreenMode(mode);
        if (intent.portraitPrompt) {
          this.callbacks.fetchPortrait(target, intent.portraitPrompt);
        }
        // Set hail state so mute button appears
        const targetName = this.getEntityName(target) ?? target.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        this.callbacks.setHailState?.({ active: true, channelMuted: false, targetId: target, targetName });
        // Face the target
        this.enqueue([{ action: "rotate", params: { entityId: "player_ship", heading: target }, delay }]);
        break;
      }

      case "close_channel": {
        setCameraMode("follow");
        this.callbacks.setViewscreenMode({ mode: "space" });
        this.callbacks.setHailState?.({ active: false, channelMuted: false, targetId: null, targetName: null });
        break;
      }

      case "incoming_hail": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] incoming_hail: no target"); break; }
        const targetName = this.getEntityName(target) ?? target;
        this.callbacks.addLogEntry?.(`Incoming hail from ${targetName}.`);
        // Set pending state — player must accept or decline
        const mode: ViewscreenMode = {
          mode: "pending_hail",
          characterId: target,
          portraitPrompt: intent.portraitPrompt,
        };
        this.callbacks.setViewscreenMode(mode);
        break;
      }

      // ── OPERATIONS ──

      case "transport_to": {
        const source = intent.source ?? "player_ship";
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] transport_to: no target"); break; }
        this.enqueue([{ action: "transporter", params: { sourceId: source, targetId: target }, delay }]);
        this.enqueue([{ action: "bridge_shake", params: { intensity: 0.3 }, delay: delay + 0.5 }]);
        const targetName = this.getEntityName(target) ?? target;
        this.callbacks.addLogEntry?.(`Transporter energizing — beaming to ${targetName}.`);
        setTimeout(() => this.callbacks.addLogEntry?.(`Transport complete.`), (delay + 3) * 1000);
        break;
      }

      case "transport_from": {
        const source = intent.source ?? "player_ship";
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] transport_from: no target"); break; }
        this.enqueue([{ action: "transporter", params: { sourceId: target, targetId: source }, delay }]);
        this.enqueue([{ action: "bridge_shake", params: { intensity: 0.3 }, delay: delay + 0.5 }]);
        const targetName = this.getEntityName(target) ?? target;
        this.callbacks.addLogEntry?.(`Transporter energizing — beaming from ${targetName}.`);
        setTimeout(() => this.callbacks.addLogEntry?.(`Transport complete. Passengers aboard.`), (delay + 3) * 1000);
        break;
      }

      case "launch_shuttle":
      case "launch_probe": {
        const source = intent.source ?? "player_ship";
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] launch: no target"); break; }
        const objectType = intent.objectType ?? (intent.type === "launch_probe" ? "probe" : "shuttle");
        this.enqueue([{ action: "launch", params: { sourceId: source, targetId: target, objectType }, delay }]);

        const targetName = this.getEntityName(target) ?? target;
        const sourcePos = this.resolveEntityPosition(source);
        const targetPos = this.resolveEntityPosition(target);
        const speed = objectType === "probe" ? 8 : 4;
        const dist = (sourcePos && targetPos) ? vec3Dist(sourcePos, targetPos) : 100;
        const duration = Math.max(5, dist / speed);

        const orderId = nextOrderId("launch");
        const label = objectType === "probe" ? `Probe en route to ${targetName}` : `Shuttle en route to ${targetName}`;
        this.timer.processOrders([{ id: orderId, action: "add", label, duration }]);
        this.callbacks.addLogEntry?.(`Launching ${objectType} toward ${targetName}.`);
        break;
      }

      case "cloak": {
        const entity = intent.source ?? "player_ship";
        this.enqueue([{ action: "cloak", params: { entityId: entity }, delay }]);
        this.callbacks.addLogEntry?.("Engaging cloaking device.");
        break;
      }

      case "decloak": {
        const entity = intent.source ?? "player_ship";
        this.enqueue([{ action: "decloak", params: { entityId: entity }, delay }]);
        this.callbacks.addLogEntry?.("Decloaking.");
        break;
      }

      // ── SPAWN / DESPAWN ──

      case "warp_in": {
        const entityId = intent.target;
        if (!entityId) { console.warn("[IntentResolver] warp_in: no target (entity ID)"); break; }
        this.enqueue([{ action: "warp_in", params: { entityId, direction: "system_edge", shipData: intent.shipData }, delay }]);
        break;
      }

      case "warp_out": {
        const entityId = intent.target;
        if (!entityId) break;
        this.enqueue([{ action: "warp_out", params: { entityId }, delay }]);
        break;
      }

      case "ship_destroyed": {
        const entityId = intent.target;
        if (!entityId) break;
        this.enqueue([{ action: "ship_destroyed", params: { entityId }, delay }]);
        const name = this.getEntityName(entityId) ?? entityId;
        this.callbacks.addLogEntry?.(`${name} destroyed.`);
        break;
      }

      // ── ENVIRONMENT ──

      case "bridge_shake": {
        this.enqueue([{ action: "bridge_shake", params: { intensity: intent.intensity ?? 1, durationSeconds: 0.8 }, delay }]);
        break;
      }

      // ── NPC ACTIONS (same as player but with source entity) ──

      case "npc_approach": {
        const source = intent.source;
        const target = intent.target;
        if (!source || !target) break;
        const speed = intent.speed ?? "slow";
        this.enqueue([{ action: "move_to", params: { entityId: source, destination: target, speed: SPEED_LABEL_TO_IMPULSE[speed] }, delay }]);
        break;
      }

      case "npc_retreat": {
        const source = intent.source;
        if (!source) break;
        const speed = intent.speed ?? "full";
        this.enqueue([{ action: "move_to", params: { entityId: source, destination: "system_edge", speed: SPEED_LABEL_TO_IMPULSE[speed] }, delay }]);
        break;
      }

      case "npc_fire": {
        const source = intent.source;
        const target = intent.target ?? "player_ship";
        if (!source) break;
        const weaponType = intent.weaponType ?? "disruptor";
        const hit = intent.hit ?? true;
        const isBeam = ["phaser", "disruptor", "cutting_beam"].includes(weaponType);
        if (isBeam) {
          this.enqueue([{ action: "fire_beam", params: { sourceId: source, targetId: target, weaponType, durationSeconds: 2, hit }, delay }]);
        } else {
          this.enqueue([{ action: "fire_torpedo", params: { sourceId: source, targetId: target, torpedoType: weaponType, count: intent.weaponCount ?? 1, hit }, delay }]);
        }
        if (hit) {
          this.enqueue([
            { action: "shield_impact", params: { entityId: target, direction: "forward", intensity: 0.8 }, delay: delay + 0.5 },
            { action: "bridge_shake", params: { intensity: 1.5, durationSeconds: 0.6 }, delay: delay + 0.5 },
          ]);
          if (intent.severity) {
            this.enqueue([{ action: "hull_damage", params: { entityId: target, severity: intent.severity, section: "hull", subsystem: intent.targetSubsystem }, delay: delay + 1 }]);
          }
        }
        break;
      }

      case "npc_raise_shields": {
        const source = intent.source;
        if (!source) break;
        this.enqueue([{ action: "raise_shields", params: { entityId: source }, delay }]);
        break;
      }

      case "npc_lower_shields": {
        const source = intent.source;
        if (!source) break;
        this.enqueue([{ action: "lower_shields", params: { entityId: source }, delay }]);
        break;
      }

      case "npc_cloak": {
        const source = intent.source;
        if (!source) break;
        this.enqueue([{ action: "cloak", params: { entityId: source }, delay }]);
        const name = this.getEntityName(source) ?? source;
        this.callbacks.addLogEntry?.(`${name} is cloaking.`);
        break;
      }

      case "npc_decloak": {
        const source = intent.source;
        if (!source) break;
        this.enqueue([{ action: "decloak", params: { entityId: source }, delay }]);
        const name = this.getEntityName(source) ?? source;
        this.callbacks.addLogEntry?.(`${name} decloaking!`);
        break;
      }

      // ── ENVIRONMENT ──

      case "set_damage_state": {
        const target = intent.target;
        if (!target) break;
        const level = intent.damageLevel ?? "damaged";
        this.enqueue([{ action: "hull_damage", params: { entityId: target, severity: level, section: "hull" }, delay }]);
        break;
      }

      // ── ENGINEERING ──

      case "divert_power": {
        const sys = intent.systemName ?? "unknown";
        const mode = intent.powerMode ?? "boosted";
        this.callbacks.updateShipSystems?.((ship) => {
          const target = ship.systems[sys as keyof typeof ship.systems];
          if (!target) return;
          target.powerAllocation = mode as any;
          if (mode === "boosted") {
            target.efficiency = Math.min(100, target.efficiency + 15);
            // Reduce two other systems
            const others = Object.keys(ship.systems).filter(k => k !== sys);
            for (const k of others.slice(0, 2)) {
              const s = ship.systems[k as keyof typeof ship.systems];
              s.efficiency = Math.max(0, s.efficiency - 10);
              s.status = s.efficiency >= 80 ? "operational" : s.efficiency >= 50 ? "degraded" : s.efficiency >= 20 ? "damaged" : "offline";
            }
          } else if (mode === "reduced") {
            target.efficiency = Math.max(0, target.efficiency - 15);
          } else if (mode === "offline") {
            target.efficiency = 0;
            target.status = "offline";
          } else {
            target.efficiency = Math.min(100, target.efficiency + 10);
          }
          target.status = target.efficiency >= 80 ? "operational" : target.efficiency >= 50 ? "degraded" : target.efficiency >= 20 ? "damaged" : "offline";
        });
        this.enqueue([{ action: "bridge_shake", params: { intensity: 0.3, durationSeconds: 0.3 }, delay }]);
        this.callbacks.addLogEntry?.(`Power rerouted: ${sys} set to ${mode}.`);
        break;
      }

      case "repair_system": {
        const sys = intent.systemName ?? "unknown";
        const orderId = nextOrderId("repair");
        const duration = 180;
        this.timer.processOrders([{ action: "add", id: orderId, label: `Repairing ${sys}`, duration }]);
        // Gradual recovery: +15 efficiency every 30s
        const oldInterval = this.activeRepairs.get(sys);
        if (oldInterval) clearInterval(oldInterval);
        const interval = setInterval(() => {
          this.callbacks.updateShipSystems?.((ship) => {
            const target = ship.systems[sys as keyof typeof ship.systems];
            if (!target) return;
            target.efficiency = Math.min(100, target.efficiency + 15);
            target.status = target.efficiency >= 80 ? "operational" : target.efficiency >= 50 ? "degraded" : target.efficiency >= 20 ? "damaged" : "offline";
          });
        }, 30000);
        this.activeRepairs.set(sys, interval);
        // Clear interval when repair finishes
        setTimeout(() => {
          clearInterval(interval);
          this.activeRepairs.delete(sys);
        }, duration * 1000);
        this.callbacks.addLogEntry?.(`Engineering dispatched to repair ${sys}.`);
        break;
      }

      case "emergency_procedure": {
        const proc = intent.procedure ?? "emergency_power";
        this.enqueue([{ action: "bridge_shake", params: { intensity: 1.5 }, delay }]);
        this.callbacks.addLogEntry?.(`Emergency procedure: ${proc.replace(/_/g, " ")}.`);
        break;
      }

      // ── TACTICAL (EXPANDED) ──

      case "modulate_shields": {
        // Visual flash + temporary shield boost
        this.enqueue([{ action: "shield_impact", params: { entityId: "player_ship", direction: "all", intensity: 0.4 }, delay }]);
        this.callbacks.updateShipSystems?.((ship) => {
          ship.shieldStrength = Math.min(100, ship.shieldStrength + 15);
        });
        // Remove boost after 10 seconds
        setTimeout(() => {
          this.callbacks.updateShipSystems?.((ship) => {
            ship.shieldStrength = Math.max(0, ship.shieldStrength - 15);
          });
        }, 10000);
        this.callbacks.addLogEntry?.("Shield frequencies rotating.");
        break;
      }

      case "set_weapon_mode": {
        const mode = intent.weaponMode ?? "precision";
        this.weaponMode = mode;
        this.callbacks.addLogEntry?.(`Weapons set to ${mode} mode.`);
        break;
      }

      case "deploy_countermeasures": {
        const cmType = intent.countermeasureType ?? "ecm";
        this.enqueue([{ action: "launch", params: { entityId: "player_ship", targetId: intent.target ?? "player_ship", objectType: "probe" }, delay }]);
        this.callbacks.addLogEntry?.(`Deploying ${cmType.toUpperCase()} countermeasures.`);
        break;
      }

      case "deploy_mines": {
        this.enqueue([{ action: "launch", params: { entityId: "player_ship", targetId: intent.target ?? "player_ship", objectType: "probe" }, delay }]);
        this.callbacks.addLogEntry?.("Mines deployed.");
        break;
      }

      case "board_ship": {
        const target = intent.target;
        if (!target) break;
        const action = intent.boardingAction ?? "assault";
        this.enqueue([{ action: "transporter", params: { entityId: "player_ship", targetId: target }, delay }]);
        const orderId = nextOrderId("board");
        this.timer.processOrders([{ action: "add", id: orderId, label: `Boarding ${this.getEntityName(target) ?? target} (${action})`, duration: 120 }]);
        this.callbacks.addLogEntry?.(`Boarding party beaming to ${this.getEntityName(target) ?? target}.`);
        break;
      }

      // ── COMMUNICATIONS (EXPANDED) ──

      case "jam_communications": {
        const target = intent.target;
        if (target) {
          this.enqueue([{ action: "scan_beam", params: { sourceId: "player_ship", targetId: target, durationSeconds: 5 }, delay }]);
        }
        const orderId = nextOrderId("jam");
        this.timer.processOrders([{ action: "add", id: orderId, label: "Jamming communications", duration: 60 }]);
        this.callbacks.addLogEntry?.("Activating communications jamming.");
        break;
      }

      case "intercept_transmission": {
        const target = intent.target;
        if (target) {
          this.enqueue([{ action: "scan_beam", params: { sourceId: "player_ship", targetId: target, durationSeconds: 8 }, delay }]);
        }
        const orderId = nextOrderId("intercept");
        this.timer.processOrders([{ action: "add", id: orderId, label: `Intercepting transmissions`, duration: 30 }]);
        this.callbacks.addLogEntry?.(`Intercepting transmissions${target ? ` from ${this.getEntityName(target) ?? target}` : ""}.`);
        break;
      }

      case "send_distress_signal": {
        this.callbacks.addLogEntry?.("Broadband distress signal transmitted on all frequencies.");
        break;
      }

      // ── OPERATIONS (EXPANDED) ──

      case "assign_away_team": {
        const target = intent.target;
        if (!target) break;
        this.enqueue([{ action: "transporter", params: { entityId: "player_ship", targetId: target }, delay }]);
        this.callbacks.addLogEntry?.(`Away team deploying to ${this.getEntityName(target) ?? target}.`);
        break;
      }

      case "recall_away_team": {
        const target = intent.target;
        if (!target) break;
        this.enqueue([{ action: "transporter", params: { entityId: target, targetId: "player_ship" }, delay }]);
        this.callbacks.addLogEntry?.("Away team returning to ship.");
        break;
      }

      case "dock": {
        const target = intent.target;
        if (!target) break;
        const targetName = this.getEntityName(target) ?? target;
        this.enqueue([{ action: "move_to", params: { entityId: "player_ship", destination: target, speed: "impulse_slow" }, delay }]);
        const orderId = nextOrderId("dock");
        this.timer.processOrders([{ action: "add", id: orderId, label: `Docking with ${targetName}`, duration: 30 }]);
        this.callbacks.addLogEntry?.(`Beginning docking approach to ${targetName}.`);
        break;
      }

      case "undock": {
        // Move 30 units away at slow impulse
        this.enqueue([{ action: "move_to", params: { entityId: "player_ship", destination: "system_edge", speed: "impulse_slow", duration: 8 }, delay }]);
        this.callbacks.addLogEntry?.("Undocking — clearing moorings.");
        break;
      }

      // ── NAVIGATION (INTER-SYSTEM) ──

      case "set_course": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] set_course: no target"); break; }
        const warp = intent.warpFactor ?? 6;
        this.callbacks.addLogEntry?.(`Course plotted to ${target}, warp ${warp}. Ready on your command.`);
        break;
      }

      case "engage_warp": {
        const warp = intent.warpFactor ?? 6;
        const duration = 8;
        const orderId = nextOrderId("warp");
        this.enqueue([
          { action: "move_to", params: { entityId: "player_ship", destination: "system_edge", speed: "warp", duration }, delay },
        ]);
        this.timer.processOrders([{ id: orderId, action: "add", label: `Warp ${warp} — jumping`, duration }]);
        this.callbacks.addLogEntry?.(`Engaging warp ${warp}.`);
        break;
      }

      // (medical_scan / assess_casualties removed in Phase 1.2 — life-sign scans are
      //  now `scan` with scanType "life_signs". Casualty reports are crew dialogue.)

      // ── SELF-DESTRUCT ──

      case "initiate_self_destruct": {
        this.enqueue([{ action: "bridge_shake", params: { intensity: 0.5, durationSeconds: 0.4 }, delay }]);
        this.callbacks.addLogEntry?.("Self-destruct sequence initiated.");
        break;
      }

      case "cancel_self_destruct": {
        this.callbacks.addLogEntry?.("Self-destruct sequence cancelled.");
        break;
      }

      // ── SALVAGE / RECOVERY ──

      case "salvage_ship": {
        const target = intent.target;
        if (!target) { console.warn("[IntentResolver] salvage_ship: no target"); break; }
        const targetName = this.getEntityName(target) ?? target;
        const duration = 120;
        const orderId = nextOrderId("salvage");
        this.enqueue([{ action: "transporter", params: { entityId: "player_ship", targetId: target }, delay }]);
        this.timer.processOrders([{ id: orderId, action: "add", label: `Salvaging ${targetName}`, duration }]);
        this.callbacks.addLogEntry?.(`Salvage team beaming to ${targetName}.`);
        break;
      }

      case "recover_escape_pods": {
        const target = intent.target;
        const targetName = target ? (this.getEntityName(target) ?? target) : "nearby escape pods";
        const duration = 30;
        const orderId = nextOrderId("recover");
        if (target) {
          this.enqueue([{ action: "tractor_beam", params: { sourceId: "player_ship", targetId: target, state: "engage" }, delay }]);
        }
        this.timer.processOrders([{ id: orderId, action: "add", label: `Recovering ${targetName}`, duration }]);
        this.callbacks.addLogEntry?.(`Recovering ${targetName}.`);
        break;
      }

      // ── VIEWSCREEN ──

      case "viewscreen_mode": {
        const target = intent.viewscreenTarget;
        if (!target) break;

        switch (target) {
          case "tactical":
            setCameraMode("tactical");
            this.callbacks.setViewscreenMode({ mode: "tactical" });
            this.callbacks.addLogEntry?.("Tactical view on screen.");
            break;
          case "magnify": {
            const entityId = intent.target;
            if (!entityId) break;
            setCameraMode("magnify", entityId);
            this.callbacks.setViewscreenMode({ mode: "magnify", targetId: entityId });
            const targetName = this.getEntityName(entityId) ?? entityId;
            this.callbacks.addLogEntry?.(`Magnifying ${targetName}.`);
            break;
          }
          case "surface_map": {
            const entityId = intent.target;
            if (!entityId) break;
            setCameraMode("follow");
            this.callbacks.setViewscreenMode({
              mode: "surface_map",
              planetId: entityId,
              markers: intent.surfaceMarkers,
            });
            this.callbacks.addLogEntry?.("Surface map on screen.");
            break;
          }
          case "scan_readout": {
            const entityId = intent.target;
            if (!entityId) break;
            setCameraMode("follow");
            this.callbacks.setViewscreenMode({
              mode: "scan_readout",
              targetId: entityId,
              scanData: intent.scanData as ScanReadoutData | undefined,
            });
            this.callbacks.addLogEntry?.("Scan readout on screen.");
            break;
          }
          case "chart":
            setCameraMode("follow");
            this.callbacks.setViewscreenMode({ mode: "chart" });
            this.callbacks.addLogEntry?.("Star chart on screen.");
            break;
          case "recording":
            setCameraMode("follow");
            this.callbacks.setViewscreenMode({
              mode: "recording",
              source: intent.recordingSource,
            });
            this.callbacks.addLogEntry?.("Playing recording on viewscreen.");
            break;
        }
        break;
      }

      case "dismiss_viewscreen": {
        setCameraMode("follow");
        this.callbacks.setViewscreenMode({ mode: "space" });
        this.callbacks.addLogEntry?.("Viewscreen returning to forward view.");
        break;
      }

      // ── UI ──

      case "show_star_chart": {
        this.callbacks.setShowChart?.(true);
        break;
      }

      case "hide_star_chart": {
        this.callbacks.setShowChart?.(false);
        break;
      }

      default:
        console.warn(`[IntentResolver] Unknown intent type: ${(intent as any).type}`);
    }
  }

  private enqueue(actions: SceneAction[]) {
    this.executor.enqueue(actions);
  }

  private getEntityName(id: string): string | undefined {
    const entity = this.executor.currentSpatialState.entities.find(e => e.id === id);
    if (entity) return entity.name;
    // Format raw IDs into readable names (e.g. "telos_iv" → "Telos IV", "player_ship" → "Player Ship")
    if (id === "player_ship") return "the ship";
    return id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()).replace(/\b(I{1,3}|Iv|Vi{0,3})\b/gi, m => m.toUpperCase());
  }

  private resolveEntityPosition(id: string): { x: number; y: number; z: number } | undefined {
    if (id === "player_ship") return this.executor.currentSpatialState.playerShip.position;
    return this.executor.currentSpatialState.entities.find(e => e.id === id)?.position;
  }

  // Get the orbit approach point — a position at orbit distance from the target,
  // along the line from the player to the target
  private resolveOrbitApproach(target: string, customDistance?: number): { x: number; y: number; z: number } {
    const entity = this.executor.currentSpatialState.entities.find(e => e.id === target);
    if (entity) {
      const t = entity.position;
      const player = this.executor.currentSpatialState.playerShip.position;
      const dx = t.x - player.x;
      const dy = t.y - player.y;
      const dz = t.z - player.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      // Orbit radius: custom distance, or default based on entity type
      const planetR = entity.planetVisuals?.radius ?? 40;
      const orbitRadius = customDistance ?? (entity.type === "planet" ? planetR * 1.625 : 20);
      // Point on the line from player to target, at orbitRadius from the target
      const ratio = Math.max(0, (dist - orbitRadius) / dist);
      return { x: player.x + dx * ratio, y: player.y + dy * ratio, z: player.z + dz * ratio };
    }
    return this.executor.currentSpatialState.playerShip.position;
  }

  // Get the orbit position near a target — guaranteed to stay outside entity radius
  private resolveOrbitPosition(target: string): { x: number; y: number; z: number } {
    const entity = this.executor.currentSpatialState.entities.find(e => e.id === target);
    if (entity) {
      const p = entity.position;
      const player = this.executor.currentSpatialState.playerShip.position;
      const dx = p.x - player.x;
      const dy = p.y - player.y;
      const dz = p.z - player.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      // Planets need more clearance than ships
      const minStop = entity.type === "planet" ? 70 : 22;
      const stopDist = Math.max(minStop, dist * 0.3);
      const ratio = Math.max(0, (dist - stopDist) / dist);
      return { x: player.x + dx * ratio, y: player.y + dy * ratio, z: player.z + dz * ratio };
    }
    return this.executor.currentSpatialState.playerShip.position;
  }
}

function vec3Dist(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
